import { useState, type FormEvent } from "react"
import { RadioGroup as RadioGroupPrimitive } from "radix-ui"

import {
  ErroDaApi,
  api,
  contaDaEntrada,
  guardarSessao,
  lembrarEntidade,
  ultimaEntidade,
  type Conta,
  type RespostaDaEntrada,
} from "../api"
import { Button } from "../componentes/Button"
import * as Icones from "../componentes/Icones"
import { Input } from "../componentes/Input"
import { MolduraDeEntrada, SOBRETITULO_DA_ENTRADA } from "../componentes/MolduraDeEntrada"
import { PasswordInput } from "../componentes/PasswordInput"
import { algarismosDoCpf, cpfValido, mascaraDeCpf } from "../formato"
import { RECUSA } from "../lib/classes"
import { cn } from "../lib/cn"

type Props = {
  aoEntrar: (conta: Conta) => void
  aviso?: string
}

const ROTULO_DA_ENTRADA =
  "mb-1 block text-(length:--texto-xs) font-bold tracking-[0.08em] text-(--tinta-2) uppercase"
const CAMPO_DA_ENTRADA =
  "h-11 px-[14px] py-3 focus-visible:border-(--marca) focus-visible:ring-[3px] focus-visible:ring-(--azul-200)"
const BOTAO_DA_ENTRADA =
  "mt-(--esp-3) h-11 bg-(--marca) px-(--esp-4) text-(length:--texto-md) tracking-[0.02em] text-white hover:bg-(--azul-destaque) active:translate-y-px"

type Escolha = { resposta: RespostaDaEntrada; marcada: number }

function mensagemDaFalha(falha: unknown): string {
  if (falha instanceof ErroDaApi) {
    return falha.codigo === 401
      ? "CPF ou senha não conferem."
      : `${falha.regra}${falha.acao ? ` ${falha.acao}` : ""}`
  }
  return "Não foi possível conectar ao sistema. Tente novamente em instantes."
}

export function Entrar({ aoEntrar, aviso }: Props) {
  const [cpf, definirCpf] = useState("")
  const [senha, definirSenha] = useState("")
  const [erro, definirErro] = useState<string | null>(null)
  const [enviando, definirEnviando] = useState(false)
  const [escolha, definirEscolha] = useState<Escolha | null>(null)

  const podeEnviar = algarismosDoCpf(cpf).length === 11 && senha !== "" && !enviando

  function concluir(resposta: RespostaDaEntrada) {
    const conta = contaDaEntrada(resposta)
    if (conta.entidade !== null) lembrarEntidade(conta.usuario_id, conta.entidade.id)
    guardarSessao(resposta.credencial, conta)
    aoEntrar(conta)
  }

  async function continuar() {
    if (escolha === null || enviando) return
    const { resposta, marcada } = escolha
    definirEnviando(true)
    definirErro(null)
    try {
      concluir(await api.trocarEntidade(marcada, resposta.credencial, { confirmaAEntrada: true }))
    } catch (falha) {
      definirErro(mensagemDaFalha(falha))
      definirEnviando(false)
    }
  }

  function voltar() {
    definirEscolha(null)
    definirErro(null)
    definirSenha("")
  }

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (!podeEnviar) return
    if (!cpfValido(cpf)) {
      definirErro("Confira o CPF: os dígitos verificadores não conferem.")
      return
    }

    definirEnviando(true)
    definirErro(null)
    try {
      const resposta = await api.entrar(algarismosDoCpf(cpf), senha)
      const entidades = resposta.entidades ?? []
      const primeira = entidades[0]
      if (!resposta.is_root && entidades.length > 1 && primeira !== undefined) {
        const lembrada = ultimaEntidade(resposta.usuario_id)
        const marcada = entidades.some((e) => e.id === lembrada)
          ? (lembrada as number)
          : (resposta.entidade?.id ?? primeira.id)
        definirEscolha({ resposta, marcada })
        definirEnviando(false)
        return
      }
      concluir(resposta)
    } catch (falha) {
      definirErro(mensagemDaFalha(falha))
      definirEnviando(false)
    }
  }

  const mensagemDeErro = erro !== null && (
    <p
      className="mt-3 rounded-(--raio-controle) border border-(--vermelho) bg-(--vermelho-fundo) px-[11px] py-[9px] text-[0.8125rem]"
      role="alert"
    >
      {erro}
    </p>
  )

  return (
    <MolduraDeEntrada>

      {escolha === null ? (
        <form className="flex flex-col" onSubmit={enviar}>
          <p className={cn(SOBRETITULO_DA_ENTRADA, "mb-(--esp-4)")}>
            Autenticação
          </p>
          <h1 className="sr-only">Acesse sua conta</h1>

          <label htmlFor="cpf" className={ROTULO_DA_ENTRADA}>
            CPF
          </label>
          <Input
            id="cpf"
            name="cpf"
            type="text"
            inputMode="numeric"
            autoComplete="username"
            placeholder="000.000.000-00"
            maxLength={14}
            className={CAMPO_DA_ENTRADA}
            value={cpf}
            onChange={(e) => definirCpf(mascaraDeCpf(e.target.value))}
            disabled={enviando}
          />

          <label htmlFor="senha" className={cn(ROTULO_DA_ENTRADA, "mt-3")}>
            Senha
          </label>
          <PasswordInput
            id="senha"
            autoComplete="current-password"
            className={CAMPO_DA_ENTRADA}
            value={senha}
            onChange={(e) => definirSenha(e.target.value)}
            disabled={enviando}
          />

          {aviso !== undefined && <p className={RECUSA}>{aviso}</p>}
          {mensagemDeErro}

          <Button className={cn(BOTAO_DA_ENTRADA, "w-full")} disabled={!podeEnviar}>
            <Icones.Entrar />
            {enviando ? "Entrando..." : "Entrar"}
          </Button>
        </form>
      ) : (
        <div className="flex flex-col">
          <h1 id="rotulo-escolha-de-entidade" className="m-0 text-(length:--texto-lg) font-(weight:--peso-forte) text-(--tinta)">
            Selecione a entidade
          </h1>
          <p className="mt-1 mb-4 text-(length:--texto-sm) text-(--tinta-2)">
            Olá, {escolha.resposta.nome}. Você pode trocar depois, no topo da tela, sem sair.
          </p>

          <RadioGroupPrimitive.Root
            aria-labelledby="rotulo-escolha-de-entidade"
            className="flex flex-col gap-2"
            value={String(escolha.marcada)}
            onValueChange={(valor) => definirEscolha({ ...escolha, marcada: Number(valor) })}
          >
            {escolha.resposta.entidades.map((entidade) => (
              <RadioGroupPrimitive.Item
                key={entidade.id}
                value={String(entidade.id)}
                aria-label={`${entidade.nome} (${entidade.sigla})`}
                disabled={enviando}
                className="flex w-full cursor-pointer items-center gap-3 rounded-(--raio-controle) border border-(--borda) bg-(--painel) px-3 py-2.5 text-left hover:border-(--borda-forte) focus-visible:ring-2 focus-visible:ring-(--marca) focus-visible:outline-none data-[state=checked]:border-(--marca) data-[state=checked]:ring-1 data-[state=checked]:ring-(--marca)"
              >
                <span
                  className="grid size-4 flex-none place-items-center rounded-full border border-(--borda-forte) bg-(--superficie)"
                  aria-hidden="true"
                >
                  <RadioGroupPrimitive.Indicator className="size-2 rounded-full bg-(--marca)" />
                </span>
                <span className="min-w-0">
                  <span className="block text-(length:--texto-sm) font-semibold text-(--tinta)">
                    {entidade.sigla}
                  </span>
                  <span className="block text-(length:--texto-xs) text-(--tinta-2)">{entidade.nome}</span>
                </span>
              </RadioGroupPrimitive.Item>
            ))}
          </RadioGroupPrimitive.Root>

          {mensagemDeErro}

          <div className="mt-(--esp-3) flex gap-2">
            <Button type="button" variant="outline" className="h-11" onClick={voltar} disabled={enviando}>
              Voltar
            </Button>
            <Button type="button" className={cn(BOTAO_DA_ENTRADA, "mt-0 flex-1")} onClick={continuar} disabled={enviando}>
              <Icones.Entrar />
              {enviando ? "Entrando..." : "Continuar"}
            </Button>
          </div>
        </div>
      )}
    </MolduraDeEntrada>
  )
}
